import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowRight,
  FiCheck,
  FiCloud,
  FiMail,
  FiPlayCircle,
  FiStar,
  FiUsers,
} from 'react-icons/fi';
import {
  BlogBody,
  BlogCard,
  BlogGrid,
  BlogImage,
  CheckIcon,
  CtaBand,
  DemoButton,
  FeatureCard,
  FeatureGrid,
  FeatureIcon,
  Hero,
  HeroButtons,
  HeroCopy,
  HeroDot,
  HeroImage,
  HeroIndicators,
  HeroSlide,
  IconNode,
  LandingButton,
  LandingPage,
  OutlineButton,
  SectionHeader,
  SectionShell,
  SecurityDiagram,
  SecurityInner,
  SecurityList,
  SecurityText,
  StatItem,
  StatsGrid,
  TestimonialContent,
  TestimonialPhoto,
} from './style';
import sideBanner from './images/side-ban.png';
import blogOne from './images/01s.png';
import blogTwo from './images/02s.png';
import testimonialBg from './images/image-bg.png';
import statsBg from './images/stat-bg.png';

const slides = [
  {
    title: 'The Control station for your life',
    copy:
      'Manage finances, records, and daily operations from one intuitive platform built for clarity, speed, and growth. Take back control of your schedule today.',
    cta: 'Try AdminCat today',
    imageAlt: 'AdminCat Dashboard',
  },
  {
    title: "Secure your family's documents",
    copy:
      'Store, organise and access all your important files from anywhere in the world with military-grade encryption and full GDPR compliance built in.',
    cta: 'Get Started Free',
    imageAlt: 'Secure Documents',
  },
  {
    title: 'Automate your daily operations',
    copy:
      'Smart reminders, automated emails, and streamlined workflows keep you ahead of every deadline without the manual overhead.',
    cta: 'Start Automating',
    imageAlt: 'Automation',
  },
];

const features = [
  {
    icon: FiUsers,
    title: 'Family Spaces',
    copy:
      'Manage your family, household, and trusted contacts in shared spaces with clear roles and permissions.',
  },
  {
    icon: FiCloud,
    title: 'Secure Clouds',
    copy:
      'Highly encrypted cloud storage for your family documents, making sure your data is always safe and accessible.',
  },
  {
    icon: FiMail,
    title: 'Smart Emails & Reminders',
    copy:
      'Automated communication tools and reminders keep you ahead of deadlines without manual overhead.',
  },
];

const updates = [
  {
    image: blogOne,
    title: 'Creating streamlined safeguarding processes with AdminCat',
  },
  {
    image: blogTwo,
    title: 'What are your document responsibilities and how can you manage them?',
  },
  {
    image: blogOne,
    title: 'Revamping everyday administration with one secure workspace',
  },
];

const securityItems = [
  'Double Encryption',
  '2FA Authentication',
  'German Servers',
  'GDPR Compliant',
];

const stats = [
  { value: '10k+', label: 'Shared Documents' },
  { value: '2GB', label: 'Storage Spaces' },
  { value: '50', label: 'Currencies Supported' },
  { value: '99%', label: 'Uptime Guaranteed' },
];

const HomePage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [activeSlide, setActiveSlide] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((currentSlide) => (currentSlide + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  React.useEffect(() => {
    if (!location.hash) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      const target = document.getElementById(location.hash.slice(1));

      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [location.hash]);

  return (
    <LandingPage>
      <Hero>
        {slides.map((slide, index) => (
          <HeroSlide key={slide.title} active={activeSlide === index}>
            <HeroCopy>
              <h1>{t(slide.title)}</h1>
              <p>{t(slide.copy)}</p>
              <HeroButtons>
                <LandingButton as={Link} to="/register">
                  {t(slide.cta)}
                </LandingButton>
                <OutlineButton as="a" href="#features">
                  <FiPlayCircle aria-hidden="true" />
                  {t('Watch Demo')}
                </OutlineButton>
              </HeroButtons>
            </HeroCopy>
            <HeroImage>
              <img src={sideBanner} alt={t(slide.imageAlt)} />
            </HeroImage>
          </HeroSlide>
        ))}

        <HeroIndicators aria-label={t('Select homepage slide')}>
          {slides.map((slide, index) => (
            <HeroDot
              key={slide.title}
              type="button"
              active={activeSlide === index}
              onClick={() => setActiveSlide(index)}
              aria-label={`${t('Show slide')} ${index + 1}`}
            />
          ))}
        </HeroIndicators>
      </Hero>

      <SectionShell id="features">
        <FeatureGrid>
          {features.map(({ icon: Icon, title, copy }, index) => (
            <FeatureCard key={title} delay={index + 1}>
              <FeatureIcon>
                <Icon aria-hidden="true" />
              </FeatureIcon>
              <h3>{t(title)}</h3>
              <p>{t(copy)}</p>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </SectionShell>

      <SectionShell id="blog" tone="muted">
        <SectionHeader>
          <h2>{t("See what's new with AdminCat")}</h2>
          <p>
            {t(
              'AdminCat updates are the best place to read about practical ways to organise documents, finances, reminders, and household administration.',
            )}
          </p>
        </SectionHeader>
        <BlogGrid>
          {updates.map(({ image, title }, index) => (
            <BlogCard key={title} delay={index + 1}>
              <BlogImage>
                <img src={image} alt="" />
              </BlogImage>
              <BlogBody>
                <h3>{t(title)}</h3>
                <Link to="/faq">
                  {t('Read more')} <FiArrowRight aria-hidden="true" />
                </Link>
              </BlogBody>
            </BlogCard>
          ))}
        </BlogGrid>
      </SectionShell>

      <CtaBand>
        <h2>{t('Want to try AdminCat out?')}</h2>
        <DemoButton as={Link} to="/register">
          {t('Get a Demo')} <FiArrowRight aria-hidden="true" />
        </DemoButton>
      </CtaBand>

      <SectionShell
        as="section"
        style={{
          backgroundImage: `linear-gradient(rgba(4, 56, 115, 0.78), rgba(4, 56, 115, 0.78)), url(${testimonialBg})`,
        }}
        variant="testimonial"
      >
        <TestimonialPhoto>
          <div className="photo-frame">
            <FiUsers aria-hidden="true" />
          </div>
          <div className="photo-label">{t('Jhon Doe, Student')}</div>
          <div className="stars" aria-label={t('Five star review')}>
            {[...Array(5)].map((_, index) => (
              <FiStar key={index} aria-hidden="true" />
            ))}
          </div>
        </TestimonialPhoto>
        <TestimonialContent>
          <span className="quote-mark">"</span>
          <blockquote>
            {t(
              'My paperwork was always a challenge. Now I consolidate and find my documents in a few seconds. You are sooo smart!',
            )}
          </blockquote>
          <cite>{t('Jhon Doe, Student')}</cite>
        </TestimonialContent>
      </SectionShell>

      <SectionShell id="security">
        <SecurityInner>
          <SecurityText>
            <h2>
              {t('German-Engineered')}
              <span>{t('Security data')}</span>
            </h2>
            <p>
              {t(
                'Your data is encrypted twice. Hosted exclusively in Frankfurt, Germany. Fully GDPR compliant. Your privacy is our priority.',
              )}
            </p>
            <SecurityList>
              {securityItems.map((item) => (
                <li key={item}>
                  <CheckIcon>
                    <FiCheck aria-hidden="true" />
                  </CheckIcon>
                  {t(item)}
                </li>
              ))}
            </SecurityList>
          </SecurityText>

          <SecurityDiagram aria-hidden="true">
            <svg viewBox="0 0 300 260">
              <line x1="150" y1="130" x2="50" y2="50" />
              <line x1="150" y1="130" x2="250" y2="50" />
              <line x1="150" y1="130" x2="50" y2="210" />
              <line x1="150" y1="130" x2="250" y2="210" />
            </svg>
            <IconNode className="top-left">
              <span className="node-emoji" role="img" aria-label="key">
                🔑
              </span>
            </IconNode>
            <IconNode className="top-right">
              <span className="node-emoji" role="img" aria-label="shield">
                🛡️
              </span>
            </IconNode>
            <IconNode className="bottom-right">
              <span className="node-emoji" role="img" aria-label="lock">
                🔒
              </span>
            </IconNode>
            <IconNode className="bottom-left">
              <span className="node-emoji" role="img" aria-label="folder">
                📁
              </span>
            </IconNode>
            <div className="diagram-center">
              <span className="center-emoji" role="img" aria-label="bank">
                🏛️
              </span>
            </div>
          </SecurityDiagram>
        </SecurityInner>
      </SectionShell>

      <SectionShell
        as="section"
        style={{
          backgroundImage: `url(${statsBg})`,
        }}
        variant="stats"
      >
        <StatsGrid>
          {stats.map(({ value, label }, index) => (
            <StatItem key={label} delay={index + 1}>
              <span className="stat-value">{value}</span>
              <span className="stat-label">{t(label)}</span>
            </StatItem>
          ))}
        </StatsGrid>
      </SectionShell>
    </LandingPage>
  );
};

export default HomePage;
